import React, { useCallback, useEffect, useRef, useState, Suspense, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

import { t } from '../../../../services/i18nService';
import IntervalSaver from '../../../../services/IntervalSaver';
import styles from './textEntry.scss';
import reactMathKeyboardService from '../../../../services/ReactMathKeyboardService';
import utilService from '../../../../services/UtilService';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import { reportError } from '../../../../services/ErrorReporting';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';

const LazyMathInputComponent = React.lazy(() => import(/* webpackChunkName: "lazy-math-keyboard" */ './LazyMathInputComponent'));

const TextEntryReactMathKeyboardInput = forwardRef((props, ref) => {
    const [isLazyMathComponentLoaded, setIsLazyMathComponentLoaded] = useState(false);
    const {
        getValidationErrorMessage,
        getFocusedMathElement,
        handleOnInput,
        handleSaveData,
        handleKeyDown,
        getElementForFocus,
        applyStylesByState,
        handleDataOnPrevValueChange,
        handleInputStylesChange,
        toggleErrorMessageClasses,
        toggleIsFlaggedClasses,
        enhanceTextareaAccessibility,
    } = reactMathKeyboardService;
    const { debounce } = utilService;
    const mathRef = useRef();
    const mathContainerRef = useRef();
    const {
        isReadOnly = false,
        defaultValue: valueFromPreviousSitting,
        isFlagged,
        model,
        questionModel,
    } = props;
    const [inputStyles, setInputStyles] = useState(null);
    const [reactMathKeyboardValue, setReactMathKeyboardValue] = useState(valueFromPreviousSitting || '');
    const [savedValue, setSavedValue] = useState(reactMathKeyboardValue);
    const [errorMessage, setErrorMessage] = useState('');
    const [lastFocusedInput, setLastFocusedInput] = useState(null);
    const [isMathRefAssigned, setIsMathRefAssigned] = useState(false);
    const lastFocusedInputRef = useRef(null);

    const smartSaver = useRef(new IntervalSaver(valueFromPreviousSitting, (value) => {
        props.callback(props.model.identifier, value ? [value] : []);
    })).current;

    const validateReactMathKeyboard = (value) => {
        setErrorMessage(getValidationErrorMessage(value));
    };

    const debouncedOnInput = useRef(debounce((value) => {
        if (value !== savedValue && mathRef.current === document.activeElement) {
            saveData(value);
        }
    }, 5000)).current;

    const onInput = (value) => {
        const callbackFunciton = (valueWithMultiplication, dataWithoutRestrictedSymbols) => {
            // This is to validate reactMathKeyboard field. It will trigger on new data on fly, so we do not need to wait
            // for 5 seconds or on blur to validate if this field is correct
            validateReactMathKeyboard(valueWithMultiplication);

            // This will trigger after user input some data and have reactMathKeyboard field in focus for 5 seconds
            // We have 2 types of saving for this input. After 5 seconds in focus with updated data, and on blur
            debouncedOnInput(dataWithoutRestrictedSymbols);
            setReactMathKeyboardValue(dataWithoutRestrictedSymbols);
        };
        handleOnInput(isCorrectMathElementFocused(), value, mathRef, callbackFunciton);
    };

    const onBlur = () => {
        if (reactMathKeyboardValue !== savedValue) saveData(reactMathKeyboardValue);
    };

    const saveData = (value) => {
        try {
            const updatedExpValue = handleSaveData(value);
            smartSaver.setValue(updatedExpValue);
            setSavedValue(value);
        } catch (error) {
            if (marketplacePropertiesService.isSymbolicMathFromatImprovementEnabled()) {
                setErrorMessage(t('interactions.textEntry.reactMathKeyboard.notAllowedValues'));
            }

            reportError(`Latex/Maxima format is not supported ${error}`, { value });
        }
    };

    const isCorrectMathElementFocused = useCallback(() => getFocusedMathElement(questionModel, model, lastFocusedInputRef), [lastFocusedInputRef]);
    const a11yIIBScreenReaderV3Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);
    const a11yIIBScreenReaderV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v5);

    const handleKeyDownEvent = event => handleKeyDown(event, mathRef, lastFocusedInputRef, a11yIIBScreenReaderV3Enabled);
    const handleMathElementFocus = useCallback((event) => {
        setLastFocusedInput(getElementForFocus(event, questionModel, model));
        const keyboardElement = document.querySelector('.react-math-keyboard-keyboard-container');
        if (keyboardElement && a11yIIBScreenReaderV3Enabled) {
            keyboardElement.style.display = 'block';
        }
    }, [questionModel, model, a11yIIBScreenReaderV3Enabled, getElementForFocus, setLastFocusedInput]);
    const handleMathElementBlur = () => setLastFocusedInput(null);
    const handleMathRefAssigned = () => setIsMathRefAssigned(true);

    const ariaLabelValueforA11YV3 = a11yIIBScreenReaderV3Enabled
        ? t('interactions.inputEntry.inputFieldAriaLabelforA11Y')
        : t('interactions.inputEntry.inputFieldAriaLabel');

    // This is used because this component uses `forwardRef` and passing `ref` to the component
    // This is done to pass values between `mathRef` to parent `ref` prop
    useImperativeHandle(ref, () => ({
        getValue: () => mathRef.current.value,
    }));

    useEffect(() => {
        // Dynamic import and update the state to render the component
        import('./LazyMathInputComponent').then(() => {
            setIsLazyMathComponentLoaded(true);
        });

        if (questionModel && model) {
            const parentElementId = `${questionModel.contentRevisionId}_${model.identifier}`;
            const mathElement = document.getElementById(parentElementId);

            if (mathElement) {
                mathElement.addEventListener('focus', handleMathElementFocus, true);
                mathElement.addEventListener('blur', handleMathElementBlur, true);

                return () => {
                    mathElement.removeEventListener('focus', handleMathElementFocus, true);
                    mathElement.removeEventListener('blur', handleMathElementBlur, true);
                };
            }
        }

        return () => {
            smartSaver.saveAndStop();
            debouncedOnInput.cancel();
        };
    }, [questionModel, model, handleMathElementFocus, handleMathElementBlur, smartSaver]);

    useEffect(() => {
        applyStylesByState(props, setInputStyles);
        if (isMathRefAssigned) {
            if (a11yIIBScreenReaderV3Enabled) {
                reactMathKeyboardService.enhanceKeyboardAccessibility();
            }
            if (a11yIIBScreenReaderV5Enabled) {
                enhanceTextareaAccessibility(mathRef, questionModel, model);
            }
        }
    }, [isReadOnly, isMathRefAssigned, a11yIIBScreenReaderV3Enabled, a11yIIBScreenReaderV5Enabled]);

    useEffect(() => {
        if (!isLazyMathComponentLoaded || !isMathRefAssigned) return;
        const computedProps = {
            valueFromPreviousSitting,
            reactMathKeyboardValue,
            validateReactMathKeyboard,
            mathRef,
        };

        const clearMathInputState = () => {
            setReactMathKeyboardValue('');
            mathRef.current.latex('');
            setErrorMessage('');
            setSavedValue('');
        };

        handleDataOnPrevValueChange(computedProps, clearMathInputState);
    }, [valueFromPreviousSitting, isLazyMathComponentLoaded, isMathRefAssigned]);

    useEffect(() => {
        lastFocusedInputRef.current = lastFocusedInput;

        document.addEventListener('keydown', handleKeyDownEvent);
        return () => {
            document.removeEventListener('keydown', handleKeyDownEvent);
        };
    }, [lastFocusedInput, handleKeyDownEvent]);

    useEffect(() => {
        handleInputStylesChange(questionModel, model, inputStyles);
    }, [inputStyles]);

    useEffect(() => {
        toggleErrorMessageClasses(questionModel, model, errorMessage);
    }, [errorMessage]);

    useEffect(() => {
        toggleIsFlaggedClasses(questionModel, model, isFlagged);
    }, [isFlagged]);

    return (
        <a
            aria-label={ariaLabelValueforA11YV3}
            dir="ltr"
            ref={mathContainerRef}
            onBlur={onBlur}
            className={styles.mathKeyboardInputContainer}
        >
            <Suspense fallback={<div>...</div>}>
                {isLazyMathComponentLoaded ? (
                    <LazyMathInputComponent
                        onInput={onInput}
                        mathRef={mathRef}
                        inputStyles={inputStyles}
                        onMathRefAssigned={handleMathRefAssigned}
                        isMathRefAssigned={isMathRefAssigned}
                    />
                ) : null}
            </Suspense>

            {!!errorMessage && (
                <span role="alert" className={styles.errorMessageContainer}>
                    <span className={styles.inputValidationErrorMessage}>
                        <i className="fa fa-times-circle" aria-hidden="true" />
                        {errorMessage}
                    </span>
                </span>
            )}
        </a>
    );
});

TextEntryReactMathKeyboardInput.propTypes = {
    isReadOnly: PropTypes.bool.isRequired,
    defaultValue: PropTypes.string.isRequired,
    isFlagged: PropTypes.bool,
    callback: PropTypes.func,
    model: PropTypes.object.isRequired,
    questionModel: PropTypes.object,
};

export default TextEntryReactMathKeyboardInput;
